import { useMemo } from 'react';
import {
  AuthtorizationHandler,
  type AuthorizationHandlerCallback,
} from '@area-restrita/shared/authorization';

import { type ILink } from '@/components/layout/AppSidebar/types';
import { useAuthSessionStore } from '@/modules/authentication/stores/authSessionStore';

export type ILinkAuth = ILink<{
  authorization?: AuthorizationHandlerCallback;
}>;

const parseAuthorization = <
  T extends { authorization?: AuthorizationHandlerCallback; items?: T[] },
>(
  links: T[],
  checkAuthorization: (...handlers: AuthtorizationHandler[]) => boolean,
): T[] =>
  links
    .map((link) => {
      let authorizedSubItems: T[] | undefined;

      if (link.items && Array.isArray(link.items)) {
        authorizedSubItems = parseAuthorization(link.items, checkAuthorization);
      }

      return {
        ...link,
        items: authorizedSubItems,
      };
    })
    .filter((link) => {
      return link.authorization ? checkAuthorization(link.authorization) : true;
    });

export const useAuthorizationLinks = <
  const T extends { authorization?: AuthorizationHandlerCallback; items?: T[] },
>(
  linkAuths: T[],
): T[] => {
  const checkAuthorization = useAuthSessionStore(
    ({ checkAuthorization }) => checkAuthorization,
  );

  const links = useMemo(
    () => parseAuthorization(linkAuths, checkAuthorization),
    [checkAuthorization, linkAuths],
  );

  return links;
};
